import { GraphQLClient } from 'graphql-request';

import type { CartAPI } from '../cart/client-api';
import type {
  AddressInput,
  AddressValidationRulesQuery,
  AddressValidationRulesQueryVariables,
  CheckoutBillingAddressUpdateMutation,
  CheckoutBillingAddressUpdateMutationVariables,
  CheckoutDetailsFragment,
  CheckoutDeliveryMethodUpdateMutation,
  CheckoutDeliveryMethodUpdateMutationVariables,
  CheckoutEmailUpdateMutation,
  CheckoutEmailUpdateMutationVariables,
  CheckoutShippingAddressUpdateMutation,
  CheckoutShippingAddressUpdateMutationVariables,
  CheckoutShippingMethodUpdateMutation,
  CheckoutShippingMethodUpdateMutationVariables,
  CountryCode,
  LanguageCodeEnum,
} from '../../saleor-graphql';
import {
  AddressValidationRulesDocument,
  CheckoutBillingAddressUpdateDocument,
  CheckoutDeliveryMethodUpdateDocument,
  CheckoutEmailUpdateDocument,
  CheckoutShippingAddressUpdateDocument,
  CheckoutShippingMethodUpdateDocument,
} from '../../saleor-graphql';

export interface AddressAPIConfig {
  client: GraphQLClient;
  locale: LanguageCodeEnum;
  getCheckoutId: () => string | null;
  cartAPI?: CartAPI; // CartAPI reference for country swap
}

export class AddressAPI {
  private client: GraphQLClient;
  private config: AddressAPIConfig;
  public cartAPI?: CartAPI;

  constructor(config: AddressAPIConfig) {
    this.client = config.client;
    this.config = config;
    this.cartAPI = config.cartAPI;
  }

  async getValidationRules(
    countryCode: CountryCode,
    countryArea?: string
  ): Promise<AddressValidationRulesQuery['addressValidationRules']> {
    try {
      const response = await this.client.request<
        AddressValidationRulesQuery,
        AddressValidationRulesQueryVariables
      >(AddressValidationRulesDocument, {
        countryCode,
        countryArea,
      });

      return response.addressValidationRules;
    } catch (error) {
      console.error('Failed to fetch address validation rules:', error);
      throw error;
    }
  }

  async updateEmail(
    email: string
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.config.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const response = await this.client.request<
        CheckoutEmailUpdateMutation,
        CheckoutEmailUpdateMutationVariables
      >(CheckoutEmailUpdateDocument, {
        id: checkoutId,
        email,
        locale: this.config.locale,
      });

      if (response.checkoutEmailUpdate?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutEmailUpdate.errors.map((e) => e.code || 'UNKNOWN_ERROR'),
        };
      }

      return {
        checkout: response.checkoutEmailUpdate?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to update email:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async updateShippingAddress(
    address: AddressInput,
    options?: { skipValidation?: boolean }
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.config.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const variables = {
        id: checkoutId,
        address,
        locale: this.config.locale,
        validationRules: options?.skipValidation
          ? { checkRequiredFields: false }
          : undefined,
      };

      console.log('[updateShippingAddress] variables:', JSON.stringify(variables, null, 2));

      const response = await this.client.request<
        CheckoutShippingAddressUpdateMutation,
        CheckoutShippingAddressUpdateMutationVariables
      >(CheckoutShippingAddressUpdateDocument, variables);

      if (response.checkoutShippingAddressUpdate?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutShippingAddressUpdate.errors.map(
            (e) => e.code || 'UNKNOWN_ERROR'
          ),
        };
      }

      return {
        checkout: response.checkoutShippingAddressUpdate?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to update shipping address:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async updateBillingAddress(
    address: AddressInput
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.config.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const response = await this.client.request<
        CheckoutBillingAddressUpdateMutation,
        CheckoutBillingAddressUpdateMutationVariables
      >(CheckoutBillingAddressUpdateDocument, {
        id: checkoutId,
        address,
        locale: this.config.locale,
      });

      if (response.checkoutBillingAddressUpdate?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutBillingAddressUpdate.errors.map(
            (e) => e.code || 'UNKNOWN_ERROR'
          ),
        };
      }

      return {
        checkout: response.checkoutBillingAddressUpdate?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to update billing address:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async updateShippingMethod(
    shippingMethodId: string
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.config.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const response = await this.client.request<
        CheckoutShippingMethodUpdateMutation,
        CheckoutShippingMethodUpdateMutationVariables
      >(CheckoutShippingMethodUpdateDocument, {
        id: checkoutId,
        shippingMethodId,
        locale: this.config.locale,
      });

      if (response.checkoutShippingMethodUpdate?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutShippingMethodUpdate.errors.map(
            (e) => e.code || 'UNKNOWN_ERROR'
          ),
        };
      }

      return {
        checkout: response.checkoutShippingMethodUpdate?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to update shipping method:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }

  async updateDeliveryMethod(
    deliveryMethodId?: string | null
  ): Promise<{ checkout: CheckoutDetailsFragment | null; errors: string[] }> {
    const checkoutId = this.config.getCheckoutId();

    if (!checkoutId) {
      return { checkout: null, errors: ['NO_CHECKOUT'] };
    }

    try {
      const variables: CheckoutDeliveryMethodUpdateMutationVariables = {
        id: checkoutId,
        locale: this.config.locale,
        ...(deliveryMethodId ? { deliveryMethodId } : {}),
      };

      console.log('[updateDeliveryMethod] variables:', JSON.stringify(variables, null, 2));

      const response = await this.client.request<
        CheckoutDeliveryMethodUpdateMutation,
        CheckoutDeliveryMethodUpdateMutationVariables
      >(CheckoutDeliveryMethodUpdateDocument, variables);

      console.log(
        '[updateDeliveryMethod] response:',
        JSON.stringify(
          {
            deliveryMethod: response.checkoutDeliveryMethodUpdate?.checkout?.shippingMethod,
            availableShippingMethods:
              response.checkoutDeliveryMethodUpdate?.checkout?.availableShippingMethods,
            errors: response.checkoutDeliveryMethodUpdate?.errors,
          },
          null,
          2
        )
      );

      if (response.checkoutDeliveryMethodUpdate?.errors.length) {
        return {
          checkout: null,
          errors: response.checkoutDeliveryMethodUpdate.errors.map(
            (e) => e.code || 'UNKNOWN_ERROR'
          ),
        };
      }

      return {
        checkout: response.checkoutDeliveryMethodUpdate?.checkout || null,
        errors: [],
      };
    } catch (error) {
      console.error('Failed to update delivery method:', error);
      return { checkout: null, errors: ['NETWORK_ERROR'] };
    }
  }
}
